import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import Table from "components/__new/common/Table";
import useLoginInfo from "hooks/useLoginInfo";
import ContentLoading from "components/__new/common/ContentLoading";
import useUrlQueryParams from "hooks/useUrlQueryParams";
import processParams from "utils/processParams";
import IconEmptyBox from "assets/images/icons/project-icons/empty-box.png";
import { TableContent } from "components/__new/common/TableContent";
import CurrencyFormat from "utils/currencyFormat";
import { DateFullFormat } from "utils/dateFormat";
import * as walletAction from "store/entities/wallet/action";
import BankInterest from "assets/images/bgs/bank-interest.svg";
import BankInterestGrey from "assets/images/bgs/bank-interest-grey.svg";
import Pagination from "components/__new/common/pagination";
import Maybe from "components/__new/common/Maybe";
import OptInOutYield from "./OptInOutYield";
import pluralize from "utils/pluralize";

const List = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const {userInfo, updateUserEarnYieldStatus} = useLoginInfo();
    const {urlQueryParams} = useUrlQueryParams();
    const { dashboardLoading, dashboard, yieldReportLoading, yieldReport: yieldReportBE,  yieldSettingsLoading,
        yieldSettings, yieldBalance, yieldBalanceLoading } = useSelector((s) => s.entities.wallet);
    const { employerInfo } = useSelector((s) => s.auth.account);
    const [listParams, setListParams] = useState({
        "page": 1,
        "limit": 10,
    });
    const [isOpenYieldOptInOut, setIsOpenYieldOptInOut] = useState(false);
    
    const getDashboard = () => {
        if (dashboardLoading === false && (dashboard?.status === undefined || dashboard?.status === "failed")){
            dispatch(walletAction.getDashboardSilent(userInfo.token));
        }
    }
    
    const getYieldSettings = () => {
        dispatch(walletAction.getYieldSettings());
        dispatch(walletAction.getYieldBalance());
    }
    
    const getYieldReport = () => {
        let params = {...listParams};

        params = processParams(urlQueryParams, params, "page", "number", 1, "page");
        params = processParams(urlQueryParams, params, "limit", "number", 10, "limit");
        params = processParams(urlQueryParams, params, "status", "string", "", "status");
        // params = processParams(urlQueryParams, params, "search", "string", "", "keyword");
        params = processParams(urlQueryParams, params, "startDate", "string", "", "startDate");
        params = processParams(urlQueryParams, params, "endDate", "string", "", "endDate");

        setListParams(params);
        dispatch(walletAction.getYieldReport(userInfo.token, dashboard?.data?.walletDetails?.walletId, params));
    }

    const transformDownloadData = (data) => {
        if (data){
            const transformedData = data?.map((e) => ({
                ...e,
                principalAmount: e.principalAmount / 100,
                interest: e.interest / 100,
            }))
            return transformedData;
        }
        else{
            return [];
        }
    }
    
    useEffect(() => {
        getYieldSettings();
        // eslint-disable-next-line
    }, [])
    
    useEffect(() => {
        if (!employerInfo?.data?.employer) return;
        updateUserEarnYieldStatus(employerInfo?.data?.employer?.shouldEarnYield);
    }, [employerInfo]);

    useEffect(() => {
        getDashboard();
        // eslint-disable-next-line
    }, []);


    useEffect(() => {
        if (dashboard?.status === "success"){
            getYieldReport();
        }
        // eslint-disable-next-line
    }, [location, dashboard]);

    // useEffect(() => {
    //     let data = [...yieldReportBE?.docs];
    //     if (yieldReportBE?.docs?.length > 0){
    //         const searchTerm = urlQueryParams["search"] ?? "";
    //         data = data.filter((report) => {
    //             if (
    //                 MonthYearFormat(report?.createdAt)?.includes(searchTerm) ||
    //                 MonthYearFormat(report?.updatedAt)?.includes(searchTerm) ||
    //                 (report?.interest/100)?.toString()?.includes(searchTerm) ||
    //                 (report?.principalAmount/100)?.toString()?.includes(searchTerm) ||
    //                 report?.rate?.toString()?.includes(searchTerm) ||
    //                 report?.walletId?.toString()?.includes(searchTerm)
    //             ){
    //                 return report;
    //             }
    //             return null;
    //         })
    //     }
    //     setYieldReport(data);
    //     // eslint-disable-next-line
    // }, [yieldReportBE?.docs]);

    return (
        <>

            <OptInOutYield
                isOpen={isOpenYieldOptInOut}
                setIsOpen={setIsOpenYieldOptInOut}
                dashboardLoading={dashboardLoading}
                dashboard={dashboard}
                employer={userInfo?.employer}
            />

            <div className='bg-ep-white rounded-2xl my-5 p-4'>
                <div className='flex flex-col lg:flex-row gap-6'>
                        <Maybe condition={userInfo?.employer?.shouldEarnYield === true}>
                            <div className='w-full lg:w-2/4 bg-left-to-right-gradient rounded-2xl pt-3'>
                                <div className="flex items-center justify-between">
                                    <div className="pl-10">
                                        <div className="mb-3 bg-ep-banner text-ep-secondary-600 rounded-full inline-block">
                                            <div className="flex items-center justify-evenly px-4 py-1 space-x-3">
                                                <div className="w-2 h-2 bg-ep-secondary rounded-full"></div>
                                                <div className="text-sm">
                                                    <ContentLoading
                                                        loading={yieldSettingsLoading}
                                                        data={yieldSettings?.interestRate + "% per annum"}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-[#374151] my-5">Total Yield Earned</div>
                                        <div className="font-bold text-3xl">
                                            <ContentLoading
                                                loading={yieldBalanceLoading}
                                                data={CurrencyFormat(yieldBalance?.data?.balance / 100, true)}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <img src={BankInterest} alt="Bank Interest" />
                                    </div>
                                </div>
                            </div>
                        </Maybe>
                        <Maybe condition={userInfo?.employer?.shouldEarnYield === false}>
                            <div className='w-full lg:w-2/4 bg-left-to-right-gradient-no-yield rounded-2xl pt-3'>
                                <div className="flex items-center justify-between">
                                    <div className="pl-10 font-medium text-2xl">
                                        <div className="mb-4">
                                            Earn Interest on your <br /> balance when you use <br /> <span className="font-semibold">Earnipay</span>
                                        </div>
                                        <div>
                                            <button type="button" className="btn btn-lg rounded-lg whitespace-nowrap text-white bg-ep-primary-600" onClick={() => setIsOpenYieldOptInOut(true)}>
                                                Opt-In For Yield
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <img src={BankInterestGrey} alt="Bank Interest" />
                                    </div>
                                </div>
                            </div>
                        </Maybe>

                    <div className='flex-1 rounded-2xl bg-[#FEF5E7] p-5'>
                        <div className="font-semibold">How It Works</div>
                        <div className="my-3 text-[#374151] font-light">
                            When you opt-in to Yield, your expense accounts
                            (your main account and any additional expense accounts you create)
                            earn daily interest hassle-free. No fees, no minimum holding time needed.
                            {/* Click the button below to learn more and maximise your finances. */}
                        </div>
                        {/* <div>
                            <button type="button" className="btn btn-lg rounded-lg whitespace-nowrap text-white bg-ep-primary-600">
                                Learn More
                            </button>
                        </div> */}
                    </div>
                </div>
            </div>
            
            <div className="mt-6 rounded-2xl box !px-0 !pb-0">
                <div className="px-4 lg:flex">
                    <div className="w-full flex justify-between">
                        <div className="font-medium text-xl flex items-center">
                            <span className="mr-4">Yield History</span>
                            <div className="label h-6 text-sm font-medium text-ep-primary-600 bg-[#ecf2f2]">
                                <ContentLoading
                                    loading={yieldReportLoading}
                                    data={<>{yieldReportBE?.totalDocs ?? 0} {pluralize(yieldReportBE?.totalDocs, "record", "records")}</>}
                                />
                            </div>
                        </div>
                        {userInfo?.employer?.shouldEarnYield === true && <div className="rounded-lg p-3 cursor-pointer text-base font-medium text-ep-danger bg-ep-danger bg-opacity-5" onClick={() => setIsOpenYieldOptInOut(true)}>
                            Opt-Out Of Yield
                        </div>}
                    </div>
                </div>


                <div className="mt-6">
                    <div>

                        <Table>
                            <Table.Head>
                                <th>Date</th>
                                <th>Balance</th>
                                <th>Interest Earned</th>
                            </Table.Head>
                            <Table.Body>

                                    <>
                                        <TableContent
                                            loading={yieldReportLoading}
                                            loadingText="Loading interest logs"
                                            data={yieldReportBE?.docs || []}
                                            colspan={7}
                                            emptyUI={<>
                                                <div className="py-20 mx-auto text-center text-base">
                                                    <div className="flex justify-center">
                                                        <img src={IconEmptyBox} alt="Empty" className="h-48" />
                                                    </div>
                                                    <div className="text-[#101828] font-medium">
                                                        No Data Available
                                                    </div>
                                                    <Maybe condition={userInfo?.employer?.shouldEarnYield === false}>
                                                        <div className="mt-1 text-ep-primary-500 cursor-pointer underline" onClick={() => setIsOpenYieldOptInOut(true)}>
                                                            Click here to opt-in for yield
                                                        </div>
                                                    </Maybe>
                                                </div>
                                            </>}
                                        />

                                        {yieldReportLoading === false && userInfo?.employer?.shouldEarnYield === true && yieldReportBE?.docs?.map((report, index) =>
                                            <tr key={index}>
                                                <td>
                                                    {DateFullFormat(report?.createdAt)}
                                                </td>
                                                <td>
                                                    {CurrencyFormat(report?.principalAmount / 100, true)}
                                                </td>
                                                <td>
                                                    {CurrencyFormat(report?.interest / 100, true)}
                                                </td>
                                            </tr>
                                        )}
                                    </>

                            </Table.Body>
                        </Table>

                        <Maybe condition={yieldReportLoading === false && yieldReportBE?.docs?.length > 0}>
                            <div className="mt-6">
                                <Pagination data={yieldReportBE} />
                            </div>
                        </Maybe>

                    </div>
                </div>
            </div>
        </>
    )
}




export default List;
